import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Star, ChevronDown, UtensilsCrossed, Flower2, Plane, BedDouble, MapPin } from "lucide-react";
import { IMAGES, LOCATIONS, AIRPORTS, HOTELS } from "../data/site";
import { Reveal, RevealWordsOnScroll } from "../components/Reveal";
import EditorialMarquee from "../components/EditorialMarquee";
import ShowcaseSlider from "../components/ShowcaseSlider";
import CTASection from "../components/CTASection";
import AnimatedCounter from "../components/AnimatedCounter";
import Lightbox from "../components/Lightbox";

/* ---------------- CINEMATIC HERO SLIDER ---------------- */
const HERO_SLIDES = [
  { src: IMAGES.venueBallroom, kicker: "The Grand Ballroom" },
  { src: IMAGES.heroBallroom, kicker: "Room for Every Guest" },
  { src: IMAGES.chandelier, kicker: "Crystal Chandeliers" },
  { src: IMAGES.ballroomElegant, kicker: "Styled to Perfection" },
];

function Hero() {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = useCallback((n) => setIndex((prev) => (n + HERO_SLIDES.length) % HERO_SLIDES.length), []);

  useEffect(() => {
    const t = setInterval(() => setIndex((p) => (p + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-navy">
      {/* Crossfading Ken-Burns slides */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 h-[118%]">
        <AnimatePresence>
          <motion.img
            key={index}
            src={HERO_SLIDES[index].src}
            alt={HERO_SLIDES[index].kicker}
            initial={{ opacity: 0, scale: 1.15 }}
            animate={{ opacity: 1, scale: 1.02 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 6.5, ease: "linear" } }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
      </motion.div>

      {/* cinematic scrim */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-navy/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-navy/30" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-24 md:px-10 md:pb-28">
        <div className="max-w-3xl animate-[vFadeUp_0.9s_cubic-bezier(0.22,1,0.36,1)_both]" style={{ animationDelay: "0.15s" }}>
          <span className="overline text-gold">Alexandria &bull; Northern Virginia</span>
        </div>
        <h1 className="mt-6 max-w-4xl font-serif text-[3.4rem] leading-[0.98] tracking-tight text-ivory sm:text-6xl md:text-7xl lg:text-[6rem] font-light">
          Where elegance meets <span className="italic text-gold">celebration</span>.
        </h1>
        <p className="mt-7 max-w-xl text-base md:text-lg font-light leading-relaxed text-ivory/80">
          A modern wedding & events venue with crystal chandeliers, in-house catering
          and decoration, and gracious service — in the heart of Northern Virginia.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/pricing" data-testid="hero-cta-pricing" className="btn-gold group">
            Request Pricing
            <ArrowUpRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/tour" data-testid="hero-cta-tour" className="btn-outline text-ivory hover:bg-ivory hover:text-navy">
            Schedule a Tour
          </Link>
        </div>

        {/* Slide indicators */}
        <div data-testid="hero-slider-dots" className="mt-12 flex items-center gap-4">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={i}
              data-testid={`hero-slide-dot-${i}`}
              onClick={() => go(i)}
              className="group flex items-center gap-2"
              aria-label={`Show ${s.kicker}`}
            >
              <span className="relative block h-[2px] w-10 overflow-hidden bg-ivory/25">
                <span
                  className="absolute inset-y-0 left-0 bg-gold transition-all duration-500"
                  style={{ width: i === index ? "100%" : "0%" }}
                />
              </span>
              <span className={`hidden text-xs font-light tracking-wide transition-colors md:inline ${i === index ? "text-gold" : "text-ivory/40 group-hover:text-ivory/70"}`}>
                {s.kicker}
              </span>
            </button>
          ))}
        </div>
      </motion.div>

      <div className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-ivory/60">
        <ChevronDown size={26} className="animate-bounce" />
      </div>
    </section>
  );
}

/* ---------------- INTRO EDITORIAL ---------------- */
function Intro() {
  return (
    <section data-testid="intro" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-36">
      <div className="grid gap-12 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <span className="overline text-gold">Vista Banquet Hall of Alexandria</span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="A venue designed around the moments that matter most." />
          </h2>
        </div>
        <div className="md:col-span-5 md:pl-8">
          <Reveal delay={0.15}>
            <p className="text-navy/70 font-light text-lg leading-relaxed">
              From intimate gatherings to grand cultural weddings, Vista pairs a light-filled
              ballroom with everything under one roof — cuisine, décor and a team who treat
              your celebration as their own.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mt-16 grid gap-4 md:grid-cols-3">
        {[
          { src: "/venue/z12.jpg", cls: "aspect-[4/5]" },
          { src: "/venue/b1.webp", cls: "aspect-[4/5] md:mt-12" },
          { src: "/venue/z03.webp", cls: "aspect-[4/5]" },
        ].map((g, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className={`group overflow-hidden rounded-sm border border-gold/25 ${g.cls}`}>
              <img src={g.src} alt="Vista Banquet Hall" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- STATS COUNTERS ---------------- */
const STATS = [
  { to: 20, suffix: "+", label: "Years Hosting Celebrations" },
  { to: 1000, suffix: "+", label: "Weddings & Events" },
  { to: 400, suffix: "", label: "Guests Seated Comfortably" },
  { to: 100, suffix: "%", label: "In-House Catering & Décor" },
];

function Stats() {
  return (
    <section data-testid="stats" className="bg-navy text-ivory">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div data-testid={`stat-${i}`} className="text-center md:text-left">
                <div className="font-serif text-5xl font-light text-gold md:text-6xl lg:text-7xl">
                  <AnimatedCounter to={s.to} suffix={s.suffix} />
                </div>
                <div className="mt-4 h-px w-10 bg-gold/40 mx-auto md:mx-0" />
                <p className="mt-4 text-sm font-light leading-relaxed text-ivory/60">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- IN-HOUSE CATERING & DECORATION ---------------- */
function Services() {
  const items = [
    {
      icon: UtensilsCrossed,
      tag: "In-House Catering",
      title: "Cuisine crafted in-house.",
      body: "No outside caterers required. Our culinary team designs plated dinners, lavish buffets and cultural menus — with full bar service and dietary accommodations.",
      img: IMAGES.cateringSpread,
    },
    {
      icon: Flower2,
      tag: "In-House Decoration",
      title: "Décor styled to your vision.",
      body: "From floral centerpieces and draping to lighting and stage design, our in-house decorators transform the hall so every detail feels effortlessly yours.",
      img: IMAGES.decorTable,
    },
  ];
  return (
    <section data-testid="services" className="bg-navy text-ivory">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <span className="overline text-gold">Everything Under One Roof</span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight">
            <RevealWordsOnScroll text="Our own catering. Our own decoration." />
          </h2>
        </div>
        <div className="grid gap-12 md:grid-cols-2 md:gap-8">
          {items.map((it, i) => (
            <Reveal key={it.tag} delay={i * 0.12}>
              <div className="group">
                <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-ivory/15">
                  <img src={it.img} alt={it.tag} className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                </div>
                <div className="mt-7 flex items-center gap-3">
                  <it.icon size={20} className="text-gold" />
                  <span className="overline text-gold">{it.tag}</span>
                </div>
                <h3 className="mt-4 font-serif text-3xl md:text-4xl font-light">{it.title}</h3>
                <p className="mt-4 text-ivory/65 font-light leading-relaxed max-w-md">{it.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PACKAGES ---------------- */
const PACKAGES = [
  { name: "Intimate", cap: "Up to 100 guests", img: IMAGES.ceremony, desc: "Perfect for smaller weddings, showers and milestone gatherings." },
  { name: "Signature", cap: "100–250 guests", img: IMAGES.ballroomElegant, desc: "Our most-loved wedding experience with full ballroom staging." },
  { name: "Grand", cap: "250+ guests", img: IMAGES.ballroomGrand, desc: "The full hall for lavish celebrations and cultural weddings." },
];

function Packages() {
  return (
    <section data-testid="packages" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
      <div className="mb-14 max-w-2xl">
        <span className="overline text-gold">Wedding Packages</span>
        <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight text-navy">
          <RevealWordsOnScroll text="Tailored packages for every celebration." />
        </h2>
      </div>
      <div className="grid gap-8 md:grid-cols-3">
        {PACKAGES.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.1}>
            <div className="group relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-gold/30">
                <img src={p.img} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                <span className="absolute left-5 top-5 overline text-ivory/90">{p.cap}</span>
              </div>
              <h3 className="mt-6 font-serif text-3xl font-light text-navy">{p.name}</h3>
              <p className="mt-2 text-navy/60 font-light">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- IMMERSIVE GALLERY ---------------- */
const GALLERY = [
  { src: IMAGES.venueBallroom, cls: "md:col-span-2 md:row-span-2" },
  { src: IMAGES.decorCandles, cls: "" },
  { src: IMAGES.ceremony, cls: "" },
  { src: IMAGES.chandelier, cls: "" },
  { src: IMAGES.floralArch, cls: "" },
];

function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  return (
    <section data-testid="gallery" className="bg-blush">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <span className="overline text-gold">The Gallery</span>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight text-navy">
              <RevealWordsOnScroll text="Moments made at Vista." />
            </h2>
          </div>
          <Link to="/tour" data-testid="gallery-cta-tour" className="btn-outline text-navy hover:bg-navy hover:text-ivory self-start">See it in Person</Link>
        </div>
        <div className="grid auto-rows-[220px] grid-cols-2 gap-4 md:grid-cols-4 md:auto-rows-[240px]">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className={g.cls}>
              <button
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightboxIndex(i)}
                className="group relative block h-full w-full overflow-hidden rounded-sm border border-gold/25"
              >
                <img src={g.src} alt="Vista Banquet Hall gallery" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 flex items-center justify-center bg-navy/0 opacity-0 transition-all duration-500 group-hover:bg-navy/30 group-hover:opacity-100">
                  <span className="overline rounded-full border border-ivory/70 px-5 py-2 text-ivory">View</span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
      <Lightbox
        images={GALLERY.map((g) => g.src)}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
}

/* ---------------- LOCATIONS ---------------- */
function Locations() {
  return (
    <section data-testid="locations" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:px-10 py-24 md:py-32">
        <div className="md:col-span-5">
          <span className="overline text-gold">Serving Northern Virginia</span>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="Close to home, wherever you're celebrating." />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-md text-navy/70 font-light leading-relaxed">
              Ideally located along Richmond Highway in Alexandria, Vista welcomes couples
              and guests from across Northern Virginia and the greater DC area.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-8 overflow-hidden rounded-sm border border-gold/30 aspect-[16/10]">
              <img src={IMAGES.arlington} alt="Northern Virginia skyline" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7 md:pl-8">
          <div className="grid gap-px bg-gold/25 border border-gold/25 sm:grid-cols-2">
            {LOCATIONS.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.08} className="bg-ivory p-8 md:p-10">
                <div className="flex items-center gap-3">
                  <MapPin size={18} className="text-gold" />
                  <h3 className="font-serif text-3xl font-light text-navy">{l.name}</h3>
                </div>
                <p className="mt-3 text-navy/60 font-light leading-relaxed">{l.note}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- GETTING HERE — AIRPORTS & HOTELS ---------------- */
function GettingHere() {
  return (
    <section data-testid="getting-here" className="bg-navy text-ivory">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <span className="overline text-gold">For Your Guests</span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight">
            <RevealWordsOnScroll text="Easy to reach. Effortless to stay." />
          </h2>
        </div>

        <div className="grid gap-14 md:grid-cols-2 md:gap-16">
          {/* Airports */}
          <div>
            <div className="flex items-center gap-3">
              <Plane size={20} className="text-gold" />
              <span className="overline text-gold">Nearby Airports</span>
            </div>
            <div className="mt-8 divide-y divide-ivory/15 border-y border-ivory/15">
              {AIRPORTS.map((a, i) => (
                <Reveal key={a.code} delay={i * 0.08}>
                  <div className="flex items-center justify-between py-6">
                    <div className="flex items-baseline gap-4">
                      <span className="font-serif text-3xl text-gold">{a.code}</span>
                      <span className="font-light text-ivory/85">{a.name}</span>
                    </div>
                    <span className="text-sm font-light text-ivory/55">{a.time}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Hotels */}
          <div>
            <div className="flex items-center gap-3">
              <BedDouble size={20} className="text-gold" />
              <span className="overline text-gold">Nearby Hotels</span>
            </div>
            <div className="mt-8 relative overflow-hidden rounded-sm border border-ivory/15">
              <img src={IMAGES.hotelExterior} alt="Nearby hotels" className="h-44 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
            </div>
            <div className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
              {HOTELS.map((h, i) => (
                <Reveal key={h.name} delay={i * 0.06}>
                  <div>
                    <p className="font-light text-ivory/90">{h.name}</p>
                    <p className="text-sm text-ivory/50">{h.area}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-xs text-ivory/45">A selection of hotels near the venue for out-of-town guests.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIALS ---------------- */
const REVIEWS = [
  { q: "Absolutely stunning venue. The chandeliers took our guests' breath away and the team handled every detail flawlessly.", a: "Sarah & Michael" },
  { q: "Having catering and décor in-house made planning so easy. Elegant, spacious, and the food was exceptional.", a: "Priya & Arjun" },
  { q: "From the tour to the last dance, the Vista team made us feel like the only clients that mattered.", a: "The Nguyen Family" },
];

function Testimonials() {
  return (
    <section data-testid="testimonials" className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
      <div className="mb-14 flex items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="overline text-gold">Kind Words</span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl font-light leading-[1.05] tracking-tight text-navy">
            <RevealWordsOnScroll text="Loved by couples across Virginia." />
          </h2>
        </div>
        <div className="hidden sm:flex text-gold">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" />)}</div>
      </div>
      <div className="grid gap-8 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={i} delay={i * 0.1} className="border border-gold/25 p-8 bg-white">
            <div className="flex text-gold mb-6">{[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}</div>
            <p className="font-serif text-2xl font-light italic leading-snug text-navy/85">“{r.q}”</p>
            <p className="mt-6 overline text-gold">{r.a}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Intro />
      <Stats />
      <ShowcaseSlider />
      <EditorialMarquee />
      <Services />
      {/* CTA after catering/decoration */}
      <CTASection
        dark={false}
        overline="Taste & Style"
        heading="See the space. Savor the details."
        body="Book a private walkthrough and discuss menus & décor with our in-house team."
        actions={[{ to: "/tour", label: "Schedule a Tour", primary: true }, { to: "/pricing", label: "Request Pricing" }]}
        testId="cta-after-services"
      />
      <Packages />
      {/* CTA after packages */}
      <CTASection
        overline="Wedding Packages"
        heading="Find the package made for your day."
        body="Share a few details and we'll send tailored pricing and our full package brochure."
        actions={[{ to: "/pricing", label: "Request Pricing", primary: true }, { to: "/contact", label: "Contact Us" }]}
        testId="cta-after-packages"
      />
      <Gallery />
      {/* CTA after gallery */}
      <CTASection
        dark={false}
        overline="Ready When You Are"
        heading="Let's reserve your date at Vista."
        body="Have a question or a date in mind? Our team is ready to help you plan."
        actions={[{ to: "/contact", label: "Contact Us", primary: true }, { to: "/tour", label: "Schedule a Tour" }]}
        testId="cta-after-gallery"
      />
      <Locations />
      <GettingHere />
      <Testimonials />
    </div>
  );
}
